import { Mail, Phone } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Overline, Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { COMPANY } from "@/data/site";

const chapters = [
    {
        number: "01",
        title: "Who We Are",
        body: [
            "AXORA PS GLOBAL LLP is a Jaipur-based merchant exporter specializing in dependable agri-commodity exports from India, sourcing export-suitable Rapeseed/Mustard DOC from verified partner processors in Rajasthan — including the Niwai sourcing hub.",
            "The company supports international buyers with sourcing, quality coordination, batch-wise COA support, export documentation, container stuffing coordination, and shipment through suitable Indian ports.",
        ],
    },
    {
        number: "02",
        title: "Our Mission",
        body: ["To make Indian agri-commodity sourcing simpler, more transparent, and more reliable for global B2B buyers."],
    },
    {
        number: "03",
        title: "Our Vision",
        body: ["To become a trusted Indian merchant export partner for feed raw materials and oilseed meals across international markets."],
    },
];

const values = [
    { title: "Reliability", text: "Committed timelines, honest communication, and shipments that match the agreed specification." },
    { title: "Compliance", text: "GST, IEC, and export documentation handled correctly on every order, every destination." },
    { title: "Quality Verification", text: "Batch-wise COA, lab test support, and third-party inspection coordination on request." },
    { title: "Clear Communication", text: "Direct access to the co-founders — no call centres, no layers between buyer and decision-maker." },
    { title: "Responsible Sourcing", text: "Material sourced only from verified partner processors across Rajasthan's oilseed belt." },
    { title: "Long-Term Relationships", text: "Built for repeat buyers — consistent quality and terms that hold across seasons." },
];

export default function About() {
    return (
        <>
            <Seo
                title="About Us — Merchant Exporter from Jaipur, India | AXORA PS GLOBAL LLP"
                description="AXORA PS GLOBAL LLP is a Jaipur-based merchant exporter of rapeseed/mustard DOC and oilseed meals, sourcing from verified partner processors in Rajasthan for global B2B buyers."
                path="/about"
                ogImage="/images/og-about.jpg"
            />

            <section data-testid="about-hero" className="relative overflow-hidden bg-white pt-[72px]">
                <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-steel-light/80 via-steel-light/30 to-white" />
                <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-14 sm:px-8 lg:px-12 lg:pb-20 lg:pt-20">
                    <Reveal>
                        <Overline>About Us</Overline>
                        <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy sm:text-5xl">
                            About AXORA PS GLOBAL LLP
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
                            A merchant export partnership built around one idea: international buyers of Indian
                            agri-commodities deserve verified quality, correct documentation, and a direct line to the
                            people responsible for their shipment.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section data-testid="about-chapters" className="border-t border-slate-200 bg-white">
                {chapters.map((chapter) => (
                    <div key={chapter.number} className="border-b border-slate-200">
                        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-6 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:px-12 lg:py-20">
                            <Reveal className="lg:col-span-3">
                                <span className="font-display text-6xl font-bold tracking-tight text-steel-light lg:text-7xl">
                                    {chapter.number}
                                </span>
                            </Reveal>
                            <Reveal delay={0.12} className="lg:col-span-9">
                                <h2 className="font-display text-2xl font-bold tracking-tight text-navy md:text-3xl">{chapter.title}</h2>
                                {chapter.body.map((para, i) => (
                                    <p key={i} className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">
                                        {para}
                                    </p>
                                ))}
                            </Reveal>
                        </div>
                    </div>
                ))}
            </section>

            <section data-testid="about-values" className="bg-mist py-24 lg:py-28">
                <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">
                    <Reveal>
                        <Overline>Chapter 04</Overline>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">What We Stand For</h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {values.map((value, i) => (
                            <Reveal key={value.title} delay={i * 0.07}>
                                <div className="h-full rounded-xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(10,37,64,0.1)]">
                                    <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-amber">{`0${i + 1}`}</p>
                                    <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-navy">{value.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{value.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section data-testid="about-founders" className="bg-white py-24 lg:py-28">
                <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">
                    <Reveal>
                        <Overline>Leadership</Overline>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Led by Its Co-Founders</h2>
                        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
                            Every inquiry reaches a co-founder directly. Specifications, pricing, COA coordination, and
                            shipment decisions are made by the people whose names are on the company.
                        </p>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {COMPANY.founders.map((founder, i) => (
                            <Reveal key={founder.name} delay={i * 0.12}>
                                <div
                                    data-testid={`founder-card-${founder.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(10,37,64,0.12)] sm:p-10"
                                >
                                    <div className="flex items-center gap-5">
                                        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-navy font-display text-xl font-bold text-white">
                                            {founder.name.split(" ").map((n) => n[0]).join("")}
                                        </span>
                                        <div>
                                            <h3 className="font-display text-2xl font-bold tracking-tight text-navy">{founder.name}</h3>
                                            <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.2em] text-steel">{founder.role}</p>
                                        </div>
                                    </div>
                                    <ul className="mt-7 space-y-3 text-sm text-slate-600">
                                        {founder.phone && (
                                            <li className="flex items-center gap-3">
                                                <Phone className="h-4 w-4 shrink-0 text-steel" />
                                                <a href={`tel:${founder.phone.replace(/\s/g, "")}`} data-testid="founder-phone-link" className="transition-colors hover:text-navy">
                                                    {founder.phone} — Phone &amp; WhatsApp
                                                </a>
                                            </li>
                                        )}
                                        <li className="flex items-center gap-3">
                                            <Mail className="h-4 w-4 shrink-0 text-steel" />
                                            <a href={`mailto:${founder.email}`} data-testid="founder-email-link" className="transition-colors hover:text-navy">
                                                {founder.email}
                                            </a>
                                        </li>
                                    </ul>
                                    <div className="mt-8 flex-1" />
                                    <WhatsAppButton
                                        label="Connect on WhatsApp"
                                        location={`founder-${founder.name.split(" ")[0].toLowerCase()}`}
                                        href={founder.whatsapp}
                                        className="w-full sm:w-auto"
                                    />
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1} className="mt-14">
                        <img
                            src="/images/buyer-meeting.jpg"
                            alt="Buyer and export partner reviewing contract and specification documents across a meeting table"
                            loading="lazy"
                            className="aspect-[21/9] w-full rounded-2xl border border-slate-200 object-cover"
                        />
                    </Reveal>
                </div>
            </section>

            <CtaBand location="about" />
        </>
    );
}
